/**
 * commercialJourneyEngine.js — Re Leve IA (Etapa 6)
 * Gerencia jornada comercial local e determinística.
 * Sem backend, sem LLM, sem banco real.
 */

import { addSessionEvent, getSessionEvents } from './localStorageEngine';
import { hasCalorieConsent } from './consentEngine';

// ── Estágios possíveis de jornada ─────────────────────────────────────────────

export const JOURNEY_STAGES = {
  CLIENTE_NOVO:         'cliente_novo',
  PRIMEIRA_RECOMPRA:    'primeira_recompra',
  RECORRENTE:           'recorrente',
  FIEL:                 'fiel',
  INATIVO:              'inativo',
  ALTO_VALOR:           'alto_valor',
  POSSIVEL_COLETIVO:    'possivel_coletivo',
  REATIVACAO:           'reativacao',
  ACOMPANHAMENTO_ATIVO: 'acompanhamento_ativo',
};

// ── Tipos de evento comercial ─────────────────────────────────────────────────

export const COMMERCIAL_EVENT_TYPES = {
  KIT_VIEWED:            'kit_viewed',
  KIT_RECOMMENDED:       'kit_recommended',
  KIT_CONFIRMED:         'kit_confirmed',
  WHATSAPP_CLICKED:      'whatsapp_clicked',
  CONSENT_REQUESTED:     'consent_requested',
  CONSENT_GRANTED:       'consent_granted',
  CALORIE_PLAN_CREATED:  'calorie_plan_created',
  MEAL_REGISTERED:       'meal_registered',
  RESTRICTION_DECLARED:  'restriction_declared',
  PROMOTION_CLICKED:     'promotion_clicked',
  REFERRAL_CLICKED:      'referral_clicked',
  HANDOFF_REQUESTED:     'handoff_requested',
  REBUY_SUGGESTED:       'rebuy_suggested',
  UPSELL_SUGGESTED:      'upsell_suggested',
  CROSS_SELL_SUGGESTED:  'cross_sell_suggested',
};

// ── Mapear segmento comercial → estágio de jornada ───────────────────────────

export function getCustomerJourneyStage(customer) {
  if (!customer) return JOURNEY_STAGES.CLIENTE_NOVO;

  const seg = (
    customer.segmentoComercial ||
    customer.segmento ||
    customer.segment ||
    ''
  ).toLowerCase();

  if (seg.includes('coletivo')) return JOURNEY_STAGES.POSSIVEL_COLETIVO;
  if (seg.includes('alto valor')) return JOURNEY_STAGES.ALTO_VALOR;
  if (seg.includes('fiel')) return JOURNEY_STAGES.FIEL;
  if (seg.includes('recorrente')) return JOURNEY_STAGES.RECORRENTE;
  if (seg.includes('inativo')) return JOURNEY_STAGES.INATIVO;
  if (seg.includes('baixo ticket')) return JOURNEY_STAGES.PRIMEIRA_RECOMPRA;
  if (seg.includes('novo')) return JOURNEY_STAGES.CLIENTE_NOVO;

  return JOURNEY_STAGES.CLIENTE_NOVO;
}

// ── Próxima melhor ação por estágio ───────────────────────────────────────────

export function getNextBestAction({ customer, consentStatus, restrictions, lastIntent, lastRecommendation }) {
  const stage = getCustomerJourneyStage(customer);
  const hasConsent = consentStatus === 'granted';
  const hasRestriction = restrictions && restrictions.length > 0;

  const actions = [];

  switch (stage) {
    case JOURNEY_STAGES.CLIENTE_NOVO:
      actions.push({ type: 'oferta', kit: 'KIT_004', label: 'Oferecer Semana Econômica', priority: 1 });
      actions.push({ type: 'oferta', kit: 'KIT_001', label: 'Oferecer Kit Dia Leve como alternativa', priority: 2 });
      break;

    case JOURNEY_STAGES.PRIMEIRA_RECOMPRA:
      actions.push({ type: 'rebuy', kit: 'KIT_004', label: 'Sugerir recompra da Semana Econômica', priority: 1 });
      actions.push({ type: 'upsell', kit: 'KIT_005', label: 'Fazer upsell para Semana Útil Resolvida', priority: 2 });
      break;

    case JOURNEY_STAGES.RECORRENTE:
      actions.push({ type: 'upsell', kit: 'KIT_005', label: 'Oferecer Semana Útil Resolvida', priority: 1 });
      actions.push({ type: 'rebuy', kit: 'KIT_005', label: 'Sugerir recompra programada', priority: 2 });
      break;

    case JOURNEY_STAGES.FIEL:
      actions.push({ type: 'upsell', kit: 'KIT_006', label: 'Oferecer Semana Completa Re Leve', priority: 1 });
      actions.push({ type: 'rebuy', kit: 'KIT_006', label: 'Sugerir recompra programada semanal', priority: 2 });
      actions.push({ type: 'cross_sell', label: 'Sugerir complementos (snack, suco, sobremesa)', priority: 3 });
      break;

    case JOURNEY_STAGES.INATIVO:
      actions.push({ type: 'reativacao', kit: 'KIT_004', label: 'Oferecer reativação com Semana Econômica', priority: 1 });
      actions.push({ type: 'oferta', kit: 'KIT_001', label: 'Oferecer Kit Dia Leve para testar', priority: 2 });
      break;

    case JOURNEY_STAGES.ALTO_VALOR:
      actions.push({ type: 'upsell', kit: 'KIT_006', label: 'Oferecer Semana Completa Re Leve', priority: 1 });
      actions.push({ type: 'cross_sell', label: 'Sugerir complementos premium', priority: 2 });
      break;

    case JOURNEY_STAGES.POSSIVEL_COLETIVO:
      actions.push({ type: 'qualificacao', label: 'Perguntar: pessoa, família ou equipe?', priority: 1 });
      actions.push({ type: 'handoff', label: 'Encaminhar para atendimento comercial', priority: 2 });
      break;

    default:
      actions.push({ type: 'oferta', kit: 'KIT_001', label: 'Oferecer Kit Dia Leve', priority: 1 });
  }

  // Se tem consentimento calórico, priorizar kit com rastreamento
  if (hasConsent) {
    actions.unshift({ type: 'calorie', label: 'Oferecer kit com registro calórico automático', priority: 0 });
  }

  // Se tem restrição, alertar sobre confirmação
  if (hasRestriction) {
    actions.push({ type: 'restricao', label: 'Confirmar disponibilidade para restrição declarada', priority: 99 });
  }

  actions.sort((a, b) => a.priority - b.priority);
  return actions[0] || { type: 'oferta', kit: 'KIT_001', label: 'Oferecer Kit Dia Leve', priority: 1 };
}

// ── Gatilhos comerciais ───────────────────────────────────────────────────────

export function getCommercialTriggers({ customer, events, consentStatus }) {
  const stage = getCustomerJourneyStage(customer);
  const triggers = [];
  const eventTypes = (events || []).map(e => e.eventType || e.type);

  if (!eventTypes.includes(COMMERCIAL_EVENT_TYPES.KIT_RECOMMENDED)) {
    triggers.push({ trigger: 'recomendacao_pendente', label: 'Nenhum kit foi recomendado ainda' });
  }

  if (!eventTypes.includes(COMMERCIAL_EVENT_TYPES.KIT_CONFIRMED)) {
    triggers.push({ trigger: 'confirmacao_pendente', label: 'Cliente não confirmou kit ainda' });
  }

  if (consentStatus !== 'granted' && !eventTypes.includes(COMMERCIAL_EVENT_TYPES.CONSENT_REQUESTED)) {
    triggers.push({ trigger: 'consentimento_nao_solicitado', label: 'Consentimento calórico não foi oferecido' });
  }

  if (stage === JOURNEY_STAGES.INATIVO) {
    triggers.push({ trigger: 'reativacao_necessaria', label: 'Cliente inativo — oferecer reativação' });
  }

  if (stage === JOURNEY_STAGES.POSSIVEL_COLETIVO) {
    triggers.push({ trigger: 'qualificacao_necessaria', label: 'Qualificar se é pessoa, família ou equipe' });
  }

  return triggers;
}

// ── Criar evento comercial ────────────────────────────────────────────────────

export function createCommercialEvent({ clienteId, type, source, payload }) {
  if (!clienteId) return null;

  const event = {
    type,
    source: source || 'chatbot',
    payload: payload || {},
    sessionId: getOrCreateSessionId(),
  };

  return addSessionEvent(clienteId, {
    eventType: type,
    ...event,
  });
}

// ── Resumo de jornada ─────────────────────────────────────────────────────────

export function getJourneySummary(clienteId) {
  if (!clienteId) return null;
  const events = getSessionEvents(clienteId);

  const confirmed = events.find(e => e.eventType === COMMERCIAL_EVENT_TYPES.KIT_CONFIRMED);
  const recommended = events.find(e => e.eventType === COMMERCIAL_EVENT_TYPES.KIT_RECOMMENDED);
  const planCreated = events.find(e => e.eventType === COMMERCIAL_EVENT_TYPES.CALORIE_PLAN_CREATED);
  const handoff = events.find(e => e.eventType === COMMERCIAL_EVENT_TYPES.HANDOFF_REQUESTED);

  return {
    clienteId,
    totalEvents: events.length,
    lastKit: confirmed?.payload?.kitName || recommended?.payload?.kitName || null,
    confirmedKit: confirmed?.payload || null,
    caloriePlanCreated: !!planCreated,
    handoffRequested: !!handoff,
    events,
  };
}

// ── Sugestão de recompra ──────────────────────────────────────────────────────

export function getRebuySuggestion(customer) {
  const stage = getCustomerJourneyStage(customer);

  switch (stage) {
    case JOURNEY_STAGES.FIEL:
    case JOURNEY_STAGES.ALTO_VALOR:
      return { kitId: 'KIT_006', message: 'Que tal repetir a Semana Completa Re Leve? Posso preparar o resumo para WhatsApp!' };
    case JOURNEY_STAGES.RECORRENTE:
      return { kitId: 'KIT_005', message: 'Posso renovar sua Semana Útil Resolvida. É só confirmar!' };
    case JOURNEY_STAGES.PRIMEIRA_RECOMPRA:
      return { kitId: 'KIT_004', message: 'Pronto para a segunda semana? Posso montar novamente!' };
    default:
      return { kitId: 'KIT_004', message: 'Posso montar uma nova semana para você. Quer repetir?' };
  }
}

// ── Sugestão de upsell ────────────────────────────────────────────────────────

export function getUpsellSuggestion(customer) {
  const stage = getCustomerJourneyStage(customer);

  switch (stage) {
    case JOURNEY_STAGES.CLIENTE_NOVO:
    case JOURNEY_STAGES.PRIMEIRA_RECOMPRA:
      return { kitId: 'KIT_005', message: 'Posso montar a Semana Útil Resolvida? Além do almoço, inclui jantar, snack e bebida para os 5 dias!' };
    case JOURNEY_STAGES.RECORRENTE:
      return { kitId: 'KIT_006', message: 'Quer experimentar a Semana Completa? 7 dias com almoço, jantar, snack, bebida e sobremesa!' };
    default:
      return null;
  }
}

// ── Sugestão de cross-sell ────────────────────────────────────────────────────

export function getCrossSellSuggestion(customer) {
  const stage = getCustomerJourneyStage(customer);

  const options = [
    { id: 'snack', label: 'Snack saudável', message: 'Posso incluir um snack saudável no seu pedido!' },
    { id: 'suco', label: 'Suco natural', message: 'Temos suco natural para complementar suas refeições.' },
    { id: 'sobremesa', label: 'Sobremesa saudável', message: 'Quer uma sobremesa saudável para completar a semana?' },
  ];

  if (stage === JOURNEY_STAGES.FIEL || stage === JOURNEY_STAGES.ALTO_VALOR) {
    options.push({ id: 'bebida_premium', label: 'Bebida especial', message: 'Para clientes especiais temos opções premium de bebida!' });
  }

  return options;
}

// ── Sugestão de retenção ──────────────────────────────────────────────────────

export function getRetentionSuggestion(customer) {
  const stage = getCustomerJourneyStage(customer);

  if (stage === JOURNEY_STAGES.INATIVO) {
    return {
      message: 'Sentimos sua falta! Que tal voltar com uma Semana Econômica? Prático, acessível e resolve seus almoços.',
      kitId: 'KIT_004',
      type: 'reativacao',
    };
  }

  if (stage === JOURNEY_STAGES.CLIENTE_NOVO) {
    return {
      message: 'O acompanhamento calórico informativo é um ótimo motivo para continuar! Quer ativar?',
      type: 'calorie_activation',
    };
  }

  return {
    message: 'Quer programar sua recompra para a próxima semana? Facilita muito a rotina!',
    type: 'rebuy_schedule',
  };
}

// ── Impacto comercial estimado ────────────────────────────────────────────────

export function getCommercialImpact(customer) {
  const stage = getCustomerJourneyStage(customer);

  return {
    recompra: {
      label: 'Recompra',
      description: 'O bot transforma interesse em renovação de kit.',
      potencial: stage === JOURNEY_STAGES.FIEL ? 'alto' : stage === JOURNEY_STAGES.RECORRENTE ? 'médio' : 'baixo',
    },
    upsell: {
      label: 'Upsell',
      description: 'O bot direciona cliente recorrente para semana útil ou semana completa.',
      potencial: stage === JOURNEY_STAGES.RECORRENTE || stage === JOURNEY_STAGES.PRIMEIRA_RECOMPRA ? 'alto' : 'médio',
    },
    cross_sell: {
      label: 'Cross-sell',
      description: 'O bot sugere snacks, sucos e sobremesas no momento certo.',
      potencial: stage === JOURNEY_STAGES.FIEL || stage === JOURNEY_STAGES.ALTO_VALOR ? 'alto' : 'médio',
    },
    retencao: {
      label: 'Retenção',
      description: 'O acompanhamento calórico cria motivo legítimo para retorno.',
      potencial: stage === JOURNEY_STAGES.INATIVO ? 'crítico' : 'alto',
    },
    handoff: {
      label: 'Encaminhamento humano',
      description: 'Compra coletiva e reclamações são encaminhadas sem perda de contexto.',
      necessario: stage === JOURNEY_STAGES.POSSIVEL_COLETIVO,
    },
  };
}

// ── SessionId local ───────────────────────────────────────────────────────────

function getOrCreateSessionId() {
  try {
    const key = 'releve_ia_session_id';
    let sid = sessionStorage.getItem(key);
    if (!sid) {
      sid = 'sess_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
      sessionStorage.setItem(key, sid);
    }
    return sid;
  } catch {
    return 'sess_fallback';
  }
}
